import { memo } from "react";
import * as images from "@/components/images/Images";
import type { NavigationMenuProps as RadixNavigationMenuProps } from "@radix-ui/react-navigation-menu";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from "@/components/ui/navigation-menu";

import type { MenuItem } from "@/lib/getMenu";
import { cn } from "@/lib/utils";
import NavigationTrigger from "./NavigationTrigger";
import NavigationLink from "./NavigationLink";
import NavigationContent from "./NavigationContent";
import NavigationSubmenuItem from "./NavigationSubmenuItem";
import { joinNestedPath, normalizePath } from "@/utils";
import NavigationSubmenuItemWithIcon from "./NavigationSubmenuItemWithIcon";
import Link from "next/link";
import { ArrowRight } from "lucide-react";
import NavigationSubmenuItemNest from "./NavigationSubmenuItemNest";
import NavigationCard from "./NavigationCard";

export type NavigationMenuProps = {
  menuItems: MenuItem[] | null;
  ref: React.Ref<HTMLDivElement | null>;
  setActiveSubIndex: (index: number) => void;
  setActiveSubMenuTitle: (title: string) => void;
  activeSubMenuTitle?: string;
  activeSubIndex: number;
} & RadixNavigationMenuProps &
  React.ComponentPropsWithoutRef<"div">;

const Navigation = ({
  menuItems,
  activeSubMenuTitle,
  setActiveSubIndex,
  setActiveSubMenuTitle,
  activeSubIndex,
  className,
  ref,
}: NavigationMenuProps) => {
  return (
    <NavigationMenu
      ref={ref}
      className={cn("mx-auto !max-w-[100vw] z-50", className)}
    >
      <NavigationMenuList>
        {menuItems?.map((item, index) => {
          const label = item.label;
          const slug = item.link;
          const submenuItems = item?.submenuItems ?? [];
          const submenuHasIcons = submenuItems?.some((item) => item.icon?.url);
          const hasNestedMenu = submenuItems?.some(
            (item) => item.nestedMenuItems
          );
          const nestedParentLink = submenuItems?.[activeSubIndex]?.link ?? "";
          const visibleNestedMenuItems =
            submenuItems?.[activeSubIndex]?.nestedMenuItems;

          return (
            <NavigationMenuItem key={index} className="relative">
              {!!submenuItems.length ? (
                <>
                  <NavigationTrigger>{label}</NavigationTrigger>
                  <NavigationContent
                    className={cn({
                      "!bg-[linear-gradient(to_right,#fff_65%,#eaeffb_35%)]":
                        hasNestedMenu,
                      "!bg-[linear-gradient(to_right,#fff_52%,#eaeffb_48%)]":
                        !hasNestedMenu && !submenuHasIcons,
                    })}
                  >
                    <div
                      className={cn(
                        "grid gap-2 p-4 md:w-[400px] lg:w-full lg:grid-cols-[1fr_.90fr]",
                        { "lg:grid-cols-[1fr_1fr_.90fr]": hasNestedMenu }
                      )}
                    >
                      <div>
                        {submenuItems.map((subItem, subIndex) => {
                          if (submenuHasIcons) {
                            return (
                              <NavigationSubmenuItemWithIcon
                                onMouseEnter={() => {
                                  setActiveSubIndex(subIndex);
                                  setActiveSubMenuTitle(subItem.title);
                                }}
                                key={subIndex}
                                href={normalizePath(subItem.link)}
                                title={subItem.title}
                                icon={subItem.icon?.url}
                                isActive={subIndex === activeSubIndex}
                              >
                                {subItem.description && (
                                  <>{subItem.description}</>
                                )}
                              </NavigationSubmenuItemWithIcon>
                            );
                          }

                          return (
                            <NavigationSubmenuItem
                              onMouseEnter={() => {
                                setActiveSubIndex(subIndex);
                                setActiveSubMenuTitle(subItem.title);
                              }}
                              key={subIndex}
                              href={normalizePath(subItem.link)}
                              title={subItem.title}
                              isActive={subIndex === activeSubIndex}
                            />
                          );
                        })}
                      </div>
                      {!!hasNestedMenu && (
                        <div className="pl-4 mt-2 space-y-2 border-l-1 border-[#eaeffb]">
                          {!!activeSubMenuTitle && (
                            <span className="text-gray-500 text-sm pl-5 flex">
                              {activeSubMenuTitle}
                            </span>
                          )}
                          {visibleNestedMenuItems?.map(
                            (nestedItem, nestedIndex) => {
                              const nestedLink = nestedItem.link;
                              const href = joinNestedPath(
                                nestedParentLink,
                                nestedLink
                              );
                              return (
                                <NavigationSubmenuItemNest
                                  key={nestedIndex}
                                  href={href}
                                  title={nestedItem.title}
                                  icon={nestedItem.icon?.url}
                                />
                              );
                            }
                          )}
                          <Link
                            href={normalizePath(nestedParentLink)}
                            className="flex gap-2 mt-4 items-center text-[#4381E5] text-sm font-medium pl-5"
                          >
                            <span>
                              Explore all {activeSubMenuTitle?.toLowerCase()}
                            </span>
                            <ArrowRight color="#4381E5" className="!w-4 !h-4" />
                          </Link>
                        </div>
                      )}
                      <div className="row-span-3 ml-10">
                        <NavigationCard
                          imageUrl={
                            submenuHasIcons ? images.menubg1 : images.menubg2
                          }
                          className="max-w-[300px] h-[290px]"
                        />
                      </div>
                    </div>
                  </NavigationContent>
                </>
              ) : (
                <NavigationLink href={slug}>{label}</NavigationLink>
              )}
            </NavigationMenuItem>
          );
        })}
      </NavigationMenuList>
    </NavigationMenu>
  );
};

export default memo(Navigation);
